import type { ActionCard as DefaultActionCardProps } from '../../../../../types/cancellation/action-card';
import type { Subscription } from '../../../../../types';

import * as styles from './index.css';
import ACCOUNT_ROUTES from '../../../../../constants/account-routes';
import ActionButtonIcon from '../../icons/action-button';
import BaseCard from '@blueshift-ui/surfaces/dist/components/base-card';
import PayAsYouGoConfirmationDialog from '../../dialogs/pay-as-you-go';
import { StructuredText } from 'react-datocms/structured-text';
import { TrackedButton } from '@blueshift-ui/analytics';
import Typography from '@blueshift-ui/theme/dist/components/typography';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import useClaimFreeMonth from '../../../../../hooks/subscription/use-claim-free-month';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

type ActionCardProps = DefaultActionCardProps & {
  subscription: Subscription;
};

function ActionCard({ actionButton, content, header, headerColor, subscription }: ActionCardProps) {
  const { makeRequestClaimFreeMonth, isLoading } = useClaimFreeMonth({
    subscriptionId: subscription.id,
  });
  const navigate = useNavigate();

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const buttonActions = {
    customerServiceChat: () => {
      window.location.replace('https://member-services.varsitytutors.com/hc');
    },
    exploreSubjects: () => {
      window.location.replace(`https://www.${process.env.VITE_DOMAIN_NAME}/my-learning/search`);
    },
    instantTutoring: () => {},
    payAsYouGo: () => setOpenConfirmationDialog(true),
    freeMonthAndRequestTutor: async () => {
      await makeRequestClaimFreeMonth({ sendEmailConfirmation: true });

      window.location.replace(
        `https://www.${process.env.VITE_DOMAIN_NAME}/my-learning/tutors?free_month_claimed=${subscription.id}&replace_tutor=true`
      );
    },
    redeemFreeMonth: async () => {
      await makeRequestClaimFreeMonth({ sendEmailConfirmation: true });

      navigate(`/${ACCOUNT_ROUTES.membership}`);
    },
    replaceTutor: () => {
      window.location.replace(
        `https://www.${process.env.VITE_DOMAIN_NAME}/my-learning/tutors?replace_tutor=true`
      );
    },
  };

  const buttonIds: Record<keyof typeof buttonActions, string> = {
    customerServiceChat: 'customer_service_chat_button',
    exploreSubjects: 'explore_subjects_button',
    instantTutoring: 'instant_tutoring_button',
    payAsYouGo: 'pay_as_you_go_button',
    freeMonthAndRequestTutor: 'free_month_and_request_tutor_button',
    redeemFreeMonth: 'redeem_free_month_button',
    replaceTutor: 'replace_tutor_button',
  };

  function handleCloseDialog() {
    setOpenConfirmationDialog(false);
  }

  return (
    <BaseCard
      actions={
        actionButton ? (
          <TrackedButton
            disabled={isLoading}
            id={buttonIds[actionButton.actionKey]}
            onClick={buttonActions[actionButton.actionKey]}
            startIcon={<ActionButtonIcon iconKey={actionButton.iconKey} />}
            variant="contained"
          >
            {actionButton.content}
          </TrackedButton>
        ) : null
      }
      className={styles.card}
    >
      {!isEmptyDocument(header) ? (
        <Typography
          className={styles.header}
          component="header"
          sx={{ backgroundColor: headerColor }}
        >
          <StructuredText data={header} />
        </Typography>
      ) : null}
      <div
        className={
          actionButton?.actionKey === 'exploreSubjects'
            ? styles.exploreSubjectsContent
            : styles.content
        }
      >
        <StructuredText
          data={content}
          renderBlock={({ record }: { record: any }) => {
            switch (record.__typename) {
              case 'SingleImageBlockRecord':
                return <img alt={record?.image?.alt} src={record?.image?.url} />;
              default:
                return null;
            }
          }}
        />
      </div>
      <PayAsYouGoConfirmationDialog
        onCancel={handleCloseDialog}
        onClose={handleCloseDialog}
        openConfirmationDialog={openConfirmationDialog}
        subscription={subscription}
      />
    </BaseCard>
  );
}

export default ActionCard;
